import React, { useState, useEffect, useRef } from 'react';


function PrintRetailLabelForm({
  onClose,
  inventory = [],
  onPrintLabelComplete,
  fetchInventory,
  userId
}) {
  const [selectedInventory, setSelectedInventory] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [printers, setPrinters] = useState([]);
  const [printerName, setPrinterName] = useState('');
  const [bpBase, setBpBase] = useState(null); // "https://localhost:9101" or "http://localhost:9100"
  const selectAllRef = useRef(null);

  // 1. Refresh inventory when modal opens
  useEffect(() => {
    fetchInventory();
  }, [fetchInventory]);

  // 2. Detect BrowserPrint + load printers
  useEffect(() => {
    const candidates = [
      'https://localhost:9101',
      'http://localhost:9100'
    ];

    async function probeBrowserPrint() {
      for (const base of candidates) {
        try {
          // Step 1: Test service
          const res = await fetch(`${base}/available`);
          if (!res.ok) continue;

          // Step 2: Get printers
          const devRes = await fetch(`${base}/available`);
          if (!devRes.ok) continue;

          const devices = await devRes.json();
          if (devices && devices.printer && Array.isArray(devices.printer)) {
            const list = devices.printer.map(p => p.name || p.uid);
            setPrinters(list);

            // Step 3: Get default printer
            const defRes = await fetch(`${base}/default?type=printer`);
            let defName = '';
            if (defRes.ok) {
              const defData = await defRes.json().catch(() => ({}));
              defName = defData.name || defData.uid || list[0] || '';
            }

            setPrinterName(defName);
            setBpBase(base);
            console.log('BrowserPrint connected via', base);
            return;
          }
        } catch (err) {
          // Try next
        }
      }

      alert('Zebra BrowserPrint is not running on this PC.\nPlease start it, then reopen Print.');
    }

    probeBrowserPrint();
  }, []);

  // 3. Filter inventory
  const filteredInventory = inventory.filter((item) =>
    (item.item_first_name?.toLowerCase() || '').includes(searchQuery.toLowerCase()) ||
    (item.item_second_name?.toLowerCase() || '').includes(searchQuery.toLowerCase()) ||
    (item.item_code?.toLowerCase() || '').includes(searchQuery.toLowerCase()) ||
    String(item.price ?? '').toLowerCase().includes(searchQuery.toLowerCase()) ||
    (item.serial_number?.toLowerCase() || '').includes(searchQuery.toLowerCase())
  );

  const visibleCodes = filteredInventory.map(i => i.item_code);
  const allVisibleSelected = visibleCodes.length > 0 &&
    visibleCodes.every(c => selectedInventory.includes(c));
  const someVisibleSelected = visibleCodes.some(c => selectedInventory.includes(c)) &&
    !allVisibleSelected;

  useEffect(() => {
    if (selectAllRef.current) {
      selectAllRef.current.indeterminate = someVisibleSelected;
    }
  }, [someVisibleSelected]);

  const handleInventorySelection = (item_code) => {
    setSelectedInventory((prev) =>
      prev.includes(item_code)
        ? prev.filter(code => code !== item_code)
        : [...prev, item_code]
    );
  };

  const toggleSelectAllVisible = () => {
    if (allVisibleSelected) {
      setSelectedInventory(prev => prev.filter(code => !visibleCodes.includes(code)));
    } else {
      setSelectedInventory(prev => Array.from(new Set([...prev, ...visibleCodes])));
    }
  };

  // 4. Build ZPL
const zplForItem = (item) => {
  const first_name    = (item.item_first_name || '').trim();
  const second_name    = (item.item_second_name || '').trim();
  const code    = (item.item_code || '').trim();      // internal item code
  const epc     = (item.rfid_epc || '').trim();       // EPC to encode
  const barcode = (item.barcode || '').trim();        // GTIN-13 / barcode value
  const p = item.price;
  const priceNum = p === null || p === undefined || p === '' ? null : Number(p);
  const priceText = Number.isFinite(priceNum) ? `$${priceNum.toFixed(2)}` : '';

  return `^XA
^LL400

^FO400,180
^A0N,55,55
^FD${first_name}^FS

^FO800,240
^A0N,55,55
^FD$${p}^FS

^FO800,300
^A0N,35,35
^FDW/GST^FS

^FO400,330
^A0N,35,35
^FD${barcode}^FS        ; print item_code as text (not the barcode)

^FO400,240
^BY2,2,100
^BCN,70,N,N,N
^FD${barcode}^FS     ; print the GTIN-13 barcode

^RS8

; --- EPC to EPC memory (keep your existing working line) ---
^RFW,H,24,1,4^FD${epc}^FS

; --- BARCODE to USER memory as ASCII (bank 3, 0–16 bytes) ---
^RFW,A,0,16,3^FD${barcode}^FS

^XZ`;
};


  // 5. Send to BrowserPrint
// 5. Send using BrowserPrint SDK (WebSocket)
async function sendToBrowserPrint(zplRaw) {
  return new Promise(async (resolve, reject) => {
    // Wait for SDK to load
    const start = Date.now();
    while (!window.BrowserPrint) {
      if (Date.now() - start > 5000) {
        return reject(new Error("BrowserPrint SDK not detected. Make sure it's running on this PC."));
      }
      await new Promise(r => setTimeout(r, 200));
    }

    window.BrowserPrint.getDefaultDevice(
      "printer",
      function (printer) {
        if (!printer) {
          // Try to get any available printer
          window.BrowserPrint.getLocalDevices(
            function (devices) {
              const first = devices.find(d => d.deviceType === "printer");
              if (first) {
                console.log("Fallback printer:", first.name || first.uid);
                first.send(zplRaw, undefined, err => err ? reject(new Error(err || "Unknown error")) : resolve(true));
              } else reject(new Error("No printer found."));
            },
            () => reject(new Error("Cannot list printers.")),
            "printer"
          );
          return;
        }

        console.log("Printing via:", printer.name || printer.uid);
        printer.send(zplRaw, undefined, function (error) {
          if (error) reject(new Error(error || "Unknown BrowserPrint error"));
          else resolve(true);
        });
      },
      function (error) {
        reject(new Error("Failed to get default printer: " + error));
      }
    );
  });
}



  // 6. Handle Print click
  // 6. Handle Print click
const handleSubmit = async (e) => {
  e.preventDefault();

  if (!selectedInventory.length) {
    alert("Please select at least one item.");
    return;
  }

  const itemsToPrint = inventory.filter(item =>
    selectedInventory.includes(item.item_code)
  );

  const withoutEpc = itemsToPrint.filter(i => !i.rfid_epc);
  if (
    withoutEpc.length > 0 &&
    !window.confirm(`${withoutEpc.length} item(s) have no RFID EPC. Continue printing anyway?`)
  ) {
    return;
  }

  try {
    const zplAll = itemsToPrint.map(zplForItem).join("\n");
    await sendToBrowserPrint(zplAll);
    alert(`Sent ${itemsToPrint.length} label(s) successfully.`);
    onPrintLabelComplete?.();
  } catch (err) {
    console.error("Print error:", err);
    alert(`Print error: ${err.message || err}`);
  }
};


  return (
    <div className="modal-overlay">
      <div className="modal-content">
        <button className="close-button" onClick={onClose}>✖</button>

        <form onSubmit={handleSubmit}>
          <h3>Print Retail Label</h3>

          {/* Search */}
          <div className="form-group">
            <label id="add-user-label">Search Inventory Items:</label>
            <input id="add-user-input"
              type="text"
              placeholder="Search by name, Product code, or serial number"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>

          {/* Printer picker */}
          <div className="form-group">
            <label id="add-user-label">Printer:</label>
            <select id="add-user-input"
              value={printerName}
              onChange={(e) => setPrinterName(e.target.value)}
              required
            >
              <option value="" disabled>Select a printer</option>
              {printers.map((p) => (
                <option key={p} value={p}>{p}</option>
              ))}
            </select>
          </div>

          {/* Table */}
          <div className="inventory-selection-container">
            <table>
              <thead>
                <tr>
                  <th className='first-child'>
                    <input
                      type="checkbox"
                      ref={selectAllRef}
                      checked={allVisibleSelected}
                      onChange={toggleSelectAllVisible}
                      aria-label="Select all visible"
                    />
                  </th>
                  <th>First Name</th>
                  <th>Second Name</th>
                  <th>Product Code</th>
                  <th>Serial Number</th>
                </tr>
              </thead>
              <tbody>
                {filteredInventory.length > 0 ? (
                  filteredInventory.map((item) => (
                    <tr key={item.item_code}>
                      <td className='first-child'>
                        <input
                          type="checkbox"
                          value={item.item_code}
                          checked={selectedInventory.includes(item.item_code)}
                          onChange={() => handleInventorySelection(item.item_code)}
                        />
                      </td>
                      <td>{item.item_first_name}</td>
                      <td>{item.item_second_name}</td>
                      <td>{item.item_code}</td>
                      <td>{item.serial_number}</td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="5">No inventory items match your search.</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          <button type="submit" className="submit-btn">Print</button>
        </form>
      </div>
    </div>
  );
}

export default PrintRetailLabelForm;
